import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";

const LOGO_URL =
  "https://dtyaaijttydrfjmxksat.supabase.co/storage/v1/object/public/public/krak%20icon.png?t=2024-12-13T05%3A57%3A20.033Z";

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signUp(email, password);
      toast({
        title: "Success",
        description: "Please check your email to confirm your account.",
      });
      navigate("/login");
    } catch (error: any) {
      if (error?.message?.includes("already registered")) {
        toast({
          title: "Error",
          description:
            "This email is already registered. Please sign in instead.",
          variant: "destructive",
        });
        navigate("/login");
      } else {
        console.error("Signup error:", error);
        toast({
          title: "Error",
          description: "Failed to create account. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-krak-gradient p-4">
      <div className="w-[400px] max-w-full bg-black/30 backdrop-blur-xl rounded-[32px] shadow-xl overflow-hidden border border-white/20">
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="text-center space-y-4 mb-8">
            <img
              src={LOGO_URL}
              alt="Krak Logo"
              className="mx-auto w-24 h-24 rounded-2xl"
              loading="eager"
            />
            <h2 className="text-2xl font-semibold text-white drop-shadow-sm">
              Create Account
            </h2>
          </div>

          <div className="space-y-4">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="h-12 rounded-xl border-white/20 bg-black/20 text-white placeholder:text-white/60 focus:bg-black/30 transition-colors"
            />

            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              minLength={6}
              className="h-12 rounded-xl border-white/20 bg-black/20 text-white placeholder:text-white/60 focus:bg-black/30 transition-colors"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-gradient-to-r from-[#735fe2] to-[#627ae2] hover:opacity-90 transition-opacity rounded-xl text-base font-medium text-white shadow-lg"
            disabled={loading}
          >
            {loading ? "Creating account..." : "Sign Up"}
          </Button>

          <p className="text-sm text-white/90 text-center drop-shadow-sm">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-white font-medium hover:text-white/90 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
